// Client OBS WebSocket v5 tối giản (Hello, Identify, Request). Chỉ cho phép đúng danh sách lệnh các trò cần dùng.

export const ALLOWED_REQUESTS = new Set([
  'GetCurrentProgramScene',
  'SetCurrentProgramScene',
  'GetSceneItemId',
  'GetSceneItemTransform',
  'SetSceneItemTransform',
  'GetSourceFilter',
  'CreateSourceFilter',
  'SetSourceFilterSettings',
  'SetSourceFilterEnabled',
  'GetInputMute',
  'SetInputMute',
  'GetVideoSettings',
]);

const b64 = (buf: ArrayBuffer) =>
  btoa(String.fromCharCode(...Array.from(new Uint8Array(buf))));
const sha256b64 = async (text: string) =>
  b64(
    await globalThis.crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(text)
    )
  );

export const obsAuth = async (
  password: string,
  salt: string,
  challenge: string
) => sha256b64((await sha256b64(password + salt)) + challenge);

type SocketLike = {
  send(data: string): void;
  close(): void;
  onopen?: (() => void) | null;
  onmessage?: ((e: { data: string }) => void) | null;
  onclose?: (() => void) | null;
  onerror?: (() => void) | null;
};

/** Lỗi OBS kèm mã trạng thái (600 là không tìm thấy nguồn, cảnh, bộ lọc). */
export type ObsError = Error & { code?: number };

const HANDSHAKE_MS = 10_000;
/** Một lệnh OBS không trả lời trong 5 giây thì coi như lỗi (trả hình, chạy trò không bị treo mãi). */
const REQUEST_MS = 5_000;

export class ObsClient {
  private socket: SocketLike | null = null;

  private pending = new Map<
    string,
    { resolve: (v: any) => void; reject: (e: Error) => void }
  >();

  private seq = 0;

  /** Huỷ lần bắt tay đang dở (khi đóng hoặc nối lại giữa chừng). */
  private abortConnect: ((e: Error) => void) | null = null;

  isConnected = false;

  private makeSocket: (url: string) => SocketLike;

  private handshakeMs: number;

  private requestMs: number;

  constructor(
    makeSocket: (url: string) => SocketLike = (url) =>
      new WebSocket(url) as unknown as SocketLike,
    handshakeMs = HANDSHAKE_MS,
    requestMs = REQUEST_MS
  ) {
    this.makeSocket = makeSocket;
    this.handshakeMs = handshakeMs;
    this.requestMs = requestMs;
  }

  connect(url: string, password: string | null): Promise<void> {
    this.close();
    return new Promise((resolve, reject) => {
      const socket = this.makeSocket(url);
      this.socket = socket;
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const fail = (e: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (this.abortConnect === fail) this.abortConnect = null;
        reject(e);
      };
      this.abortConnect = fail;
      // OBS không chào hay không trả lời bắt tay trong 10 giây: đóng và báo lỗi để trang thử lại.
      timer = setTimeout(() => {
        if (settled) return;
        fail(new Error('OBS không trả lời'));
        if (this.socket === socket) this.close();
        else socket.close();
      }, this.handshakeMs);
      // Sự kiện của socket cũ (đóng trễ sau khi đã nối socket mới) bị bỏ qua.
      const current = () => this.socket === socket;
      socket.onmessage = async (e) => {
        if (!current()) return;
        const msg = JSON.parse(e.data);
        if (msg.op === 0) {
          const auth = msg.d?.authentication;
          const authentication =
            auth && password
              ? await obsAuth(password, auth.salt, auth.challenge)
              : undefined;
          if (!current()) return;
          socket.send(
            JSON.stringify({
              op: 1,
              d: {
                rpcVersion: 1,
                ...(authentication ? { authentication } : {}),
                eventSubscriptions: 0,
              },
            })
          );
        } else if (msg.op === 2) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (this.abortConnect === fail) this.abortConnect = null;
          this.isConnected = true;
          resolve();
        } else if (msg.op === 7) {
          const p = this.pending.get(msg.d.requestId);
          if (!p) return;
          this.pending.delete(msg.d.requestId);
          if (msg.d.requestStatus?.result) p.resolve(msg.d.responseData ?? {});
          else {
            const err: ObsError = new Error(
              msg.d.requestStatus?.comment ||
                `OBS lỗi ${msg.d.requestStatus?.code}`
            );
            err.code = msg.d.requestStatus?.code;
            p.reject(err);
          }
        }
      };
      socket.onclose = () => {
        if (!current()) return;
        this.socket = null;
        this.isConnected = false;
        this.rejectPending();
        fail(new Error('Không nối được OBS'));
      };
      socket.onerror = () => socket.close();
    });
  }

  call<T = any>(
    requestType: string,
    requestData?: Record<string, unknown>
  ): Promise<T> {
    if (!ALLOWED_REQUESTS.has(requestType))
      return Promise.reject(
        new Error(`Lệnh OBS ${requestType} không được phép`)
      );
    const { socket } = this;
    if (!socket || !this.isConnected)
      return Promise.reject(new Error('Chưa nối OBS'));
    this.seq += 1;
    const requestId = `cab-${this.seq}`;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (!this.pending.delete(requestId)) return;
        reject(new Error(`OBS không trả lời lệnh ${requestType}`));
      }, this.requestMs);
      this.pending.set(requestId, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      });
      socket.send(
        JSON.stringify({ op: 6, d: { requestType, requestId, requestData } })
      );
    });
  }

  close() {
    const { socket } = this;
    this.socket = null;
    this.isConnected = false;
    this.rejectPending();
    this.abortConnect?.(new Error('Đã đóng kết nối OBS'));
    socket?.close();
  }

  private rejectPending() {
    this.pending.forEach((p) => p.reject(new Error('Mất kết nối OBS')));
    this.pending.clear();
  }
}
